/**
 * Builds the `FontGroup` a theme carries, registering its faces under generated
 * family names.
 *
 * `@repo/theme` declares the group but can't fill it: registering a face needs
 * `expo-font`, and the package stays free of a font runtime so the Preact
 * teaser can import tokens without one. This is the seam where that happens.
 *
 * Faces are named `{group}-{slot}` rather than by typeface, so the family a
 * token resolves to and the family the loader registers are generated from one
 * value and can't drift. Naming them per group rather than per theme lets
 * several themes share a face set while a sub-brand can still bring its own.
 */

import type { DefineFontGroup } from '@repo/theme';
import { FONT_SLOTS } from '@repo/theme';
import * as Font from 'expo-font';

/**
 * `loadFontAssets` stays deferred until the group is used, so a silo on a
 * partner typeface never resolves another group's faces. Its modules arrive as
 * `unknown` because `@repo/theme` has no font runtime to type them; the cast to
 * `Font.FontSource` happens here. A failed load is logged with the error — for
 * a missing file, the message names it — and never thrown.
 */
export const defineFontGroup: DefineFontGroup = ({ name, fallbacks, loadFontAssets }) => ({
    name,
    fallbacks,
    load: async () => {
        try {
            const assets = await loadFontAssets();
            const facesByFamily = Object.fromEntries(FONT_SLOTS.map((slot) => [`${name}-${slot}`, assets[slot]]));
            await Font.loadAsync(facesByFamily as Record<string, Font.FontSource>);
        } catch (error) {
            console.error(`Loading ${name} fonts failed`, error);
        }
    },
});
